import React from 'react'
import { MapPin, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Modal from '../modal'
import { GoogleMap } from './google_map'
import AddressAutocomplete from './autocomplete'
import DebouncedInput from '../debounced_input'
import DebouncedTextarea from '../debounced_textarea'
import RequiredHint from '../required_hint'

class Address extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      editing: false,
      show_map: false
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.editing !== this.state.editing)
      return true

    if (nextState.show_map !== this.state.show_map)
      return true

    if (nextProps.formatted_address !== this.props.formatted_address)
      return true

    if (nextProps.address !== this.props.address)
      return true

    if (nextProps.loaded !== this.props.loaded)
      return true

    if (nextProps.markers !== this.props.markers)
      return true

    if (nextProps.required !== this.props.required)
      return true

    return false
  }

  onEditAddress = (e) => {
    if (!this.props.loaded && this.props.store) {
      this.props.store.emit('ui:google_map:load')
    }
    this.setState({editing: !this.state.editing})
  }

  onPlaceChanged = (place) => {
    this.props.onChange({
      formatted_address: place.formatted_address,
      address_components: place.address_components,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng()
    })
  }

  onFlatUnitEstateChange = (value) => {
    this.props.onChange({flat_unit_estate: value})
  }

  onNoteChange = (value) => {
    this.props.onChange({note: value})
  }

  onShowMap = () => {
    this.setState({show_map: true})
  }

  renderContent() {
    return (
      <div className="grid gap-4">
        {(this.state.editing && this.props.loaded) ?
          <React.Fragment>
            <div className="grid gap-2">
              <Label>Search for address</Label>
              <AddressAutocomplete onPlaceChanged={this.onPlaceChanged}/>
            </div>
            <div className="grid gap-2">
              <Label>Flat/Unit/Estate</Label>
              <DebouncedInput value={this.props.address.flat_unit_estate} placeholder="e.g. Unit 2" onChange={this.onFlatUnitEstateChange}/>
            </div>
            <div className="grid gap-2">
              <Label>Note</Label>
              <DebouncedTextarea value={this.props.address.note} placeholder="Note" rows='2' onChange={this.onNoteChange}/>
            </div>
          </React.Fragment>
          : null
        }
        {(this.state.editing || (this.state.show_map && this.props.formatted_address)) ?
          <GoogleMap
            latitude={this.props.address.latitude}
            longitude={this.props.address.longitude}
            markers={this.props.markers}
            loaded={this.props.loaded}
            store={this.props.store}
          />
        :
          <div className="text-sm">
            <div>
              {this.props.formatted_address || 'None specified'}
            </div>
            {this.props.flat_unit_estate ?
              <div className="text-muted-foreground">
                Flat/Unit/Estate: {this.props.flat_unit_estate}
              </div>
              : null
            }
            {this.props.address_type ?
              <div className="text-muted-foreground">
                Type: {this.props.address_type}
              </div>
              : null
            }
            {this.props.address_note ?
              <div className="text-muted-foreground">
                Note: {this.props.address_note}
              </div>
              : null
            }
            {this.props.formatted_address ?
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" onClick={this.onShowMap}>Show map</Button>
              </div>
              : null
            }
          </div>
        }
      </div>
    )
  }

  render() {
    if (this.props.embedded) {
      return (
        <div>
          <div className="mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Address</span>
          </div>
          <div className="text-sm">
            {this.props.formatted_address || 'None specified'}
            {this.props.flat_unit_estate && <span className="ml-1 text-muted-foreground">({this.props.flat_unit_estate})</span>}
            {this.props.address_type && <span className="ml-1 text-muted-foreground">· {this.props.address_type}</span>}
            {this.props.address_note && <div className="text-xs text-muted-foreground">Note: {this.props.address_note}</div>}
          </div>
          <div className="mt-2">
            <Button variant="outline" size="sm" onClick={this.onEditAddress}><Pencil className="size-3.5"/>Edit address</Button>
          </div>
          {this.state.editing &&
            <Modal title="Edit Address" size="modal-lg" onClose={() => this.setState({editing: false})}>
              <div className="grid gap-4 p-6 pt-4">
                <div className="grid gap-2">
                  <Label>Search for address</Label>
                  {this.props.loaded ?
                    <AddressAutocomplete onPlaceChanged={this.onPlaceChanged}/>
                  :
                    <Input type="text" placeholder="Loading Google Maps..." disabled />
                  }
                </div>
                <div className="grid gap-2">
                  <Label>Flat/Unit/Estate</Label>
                  <DebouncedInput value={this.props.address.flat_unit_estate} placeholder="e.g. Unit 2" onChange={this.onFlatUnitEstateChange}/>
                </div>
                <div className="grid gap-2">
                  <Label>Note</Label>
                  <DebouncedTextarea value={this.props.address.note} placeholder="Note" rows='2' onChange={this.onNoteChange}/>
                </div>
                {this.props.loaded &&
                  <GoogleMap
                    latitude={this.props.address.latitude}
                    longitude={this.props.address.longitude}
                    markers={this.props.markers}
                    loaded={this.props.loaded}
                    store={this.props.store}
                  />
                }
              </div>
            </Modal>
          }
        </div>
      )
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="size-4" />
            Address
            {this.props.required ? <RequiredHint/> : null}
          </CardTitle>
          <CardAction className="ml-auto self-center">
            <Button variant="outline" size="sm" onClick={this.onEditAddress}><Pencil className="size-3.5"/></Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          {this.renderContent()}
        </CardContent>
      </Card>
    )
  }
}

export default Address
